import {
  Building2,
  Gauge,
  FileText,
  IndianRupee,
  ListChecks,
  MessageSquareText,
  Radar,
  Scale,
  ScanFace,
  ScrollText,
  ShieldCheck,
  Smartphone,
  Tags,
  UserCog,
  type LucideIcon,
} from "lucide-react";
import type { AdminAction } from "@/shared/auth/roles";
import { ROUTES } from "./routes";

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Role gate (rules/01 §10): the admin action this section manages; null = every operator. */
  adminAction: AdminAction | null;
  /** Highlight only on this exact path, for a section whose sibling page lives under its address (`/sms` and `/sms/templates`). */
  end?: boolean;
}

/** The ONE nav config; the sidebar is generated from it and an inventory test locks it to the role catalogue. */
export const NAV_ITEMS: readonly NavItem[] = [
  { id: "tenants", label: "Tenants", to: ROUTES.tenants, icon: Building2, adminAction: null },
  // M11 (ADR-0015 §5): money sits between the organisations and the hardware, because that is how an
  // operator's day runs — who owes what, then which terminal is down. Billing is read-only, so any
  // operator may open it; the Catalogue changes what everyone is charged, so it is admin only.
  { id: "billing", label: "Billing", to: ROUTES.billing, icon: IndianRupee, adminAction: null },
  { id: "catalogue", label: "Catalogue", to: ROUTES.catalogue, icon: Tags, adminAction: "catalog.manage" },
  // M20 (ADR-0024): the platform's SMS price and limits, and the registry of Nexborg's DLT templates. Both
  // are read by any operator — the money-and-product pattern of Billing and Catalogue — and written only by
  // an admin, which each page gates itself (so an operator triaging a stuck message can still see the
  // template and the rate). The delivery backlog is reached from the overview strip, not from the sidebar.
  { id: "sms", label: "SMS", to: ROUTES.sms, icon: MessageSquareText, adminAction: null, end: true },
  {
    id: "sms-templates",
    label: "SMS templates",
    to: ROUTES.smsTemplates,
    icon: FileText,
    adminAction: null,
  },
  // M26 (ADR-0031, contracts §22): the statutory rule sets and tax years every organisation's payroll computes
  // from. Law is read by any operator — the same split as the SMS templates — and written only by an admin
  // (`payroll_law.manage`), which each page gates itself, so no operator is hidden from the figures they would
  // be asked about in a support call.
  { id: "payroll-law", label: "Payroll law", to: ROUTES.payrollLaw, icon: Scale, adminAction: null },
  {
    id: "mobile-releases",
    label: "Mobile releases",
    to: ROUTES.mobileReleases,
    icon: Smartphone,
    adminAction: null,
  },
  { id: "fleet", label: "Fleet", to: ROUTES.fleet, icon: ScanFace, adminAction: null },
  // M14 follow-up (ADR-0007 addendum 2026-09-15): how many people fit on a terminal model. Any
  // operator reads it (the same split as Billing/Catalogue above); only an admin edits or verifies a
  // number, gated inside the page itself rather than by hiding the section — an operator triaging the
  // fleet should be able to see which numbers are real without needing an admin to look over a shoulder.
  {
    id: "device-models",
    label: "Device models",
    to: ROUTES.deviceModels,
    icon: Gauge,
    adminAction: null,
  },
  { id: "unclaimed", label: "Unclaimed", to: ROUTES.unclaimed, icon: Radar, adminAction: null },
  { id: "queues", label: "Queues", to: ROUTES.queues, icon: ListChecks, adminAction: null },
  { id: "audit", label: "Audit", to: ROUTES.audit, icon: ScrollText, adminAction: null },
  {
    id: "operators",
    label: "Operators",
    to: ROUTES.operators,
    icon: UserCog,
    adminAction: "operators.manage",
  },
  {
    id: "reviewers",
    label: "Reviewers",
    to: ROUTES.reviewers,
    icon: ShieldCheck,
    adminAction: "reviewers.manage",
  },
];
